import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Popover, Spin } from 'antd';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import useMountEffect from '../../hooks/useMountEffect';
import useValueChangeEffect from '../../hooks/useValueChangeEffect';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { retrieveCachedUserDetails } from '../../thunks/session_thunks';
import {
	downloadStoppedCreationCountsCSVForBuyer,
	downloadStoppedCreationCountsCSVForSupplier,
	fetchStoppedCreationCountsForBuyer,
	fetchStoppedCreationCountsForSupplier,
} from '../../thunks/work_orders_thunks';
import { StoppedCreationCounts } from '../../types/stopped_creation_counts';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { cn } from '../../utils/ClassNamesUtils';
import OWButton from '../ow_button/OWButton';

interface DuplicateWorkOrdersStoppedProps {
	userType: string;
	fetchStoppedCreationCounts: () => Promise<StoppedCreationCounts>;
	downloadStoppedCreationCountsCSV: () => Promise<any>;
}

// The indicator stays out of the top bar until the number is worth interrupting for.
const MIN_STOPPED_COUNT_TO_DISPLAY = 15;

/**
 * Where the last count this user saw is parked between visits, so the roll can run on the load
 * after the number moved rather than only for someone sitting on the page across a refresh.
 *
 * Keyed by workspace and email, not stored flat: this app switches workspaces in place and
 * supports impersonation, so a flat key would carry one tenant's number into another and roll
 * the pill from a total that was never this user's.
 */
const LAST_SEEN_COUNT_STORAGE_PREFIX = 'duplicateWorkOrdersStopped.lastSeenAllTime';

const getLastSeenCountStorageKey = () => {
	const cachedUserDetails = retrieveCachedUserDetails(['userType', 'workspaceId', 'user']);
	const email = nullSafeGetOrElse('user.email', cachedUserDetails, '');
	return `${LAST_SEEN_COUNT_STORAGE_PREFIX}.${cachedUserDetails.userType}.${cachedUserDetails.workspaceId}.${email}`;
};

// Storage is unavailable in some privacy modes and full in others; a missing or unreadable
// value just means "nothing seen yet", which is already the first-visit case.
const readLastSeenCount = (): number => {
	try {
		const storedCount = Number(localStorage.getItem(getLastSeenCountStorageKey()));
		return Number.isFinite(storedCount) && storedCount > 0 ? storedCount : 0;
	} catch (error) {
		return 0;
	}
};

const writeLastSeenCount = (count: number) => {
	try {
		localStorage.setItem(getLastSeenCountStorageKey(), String(count));
	} catch (error) {
		// Losing the value costs one missed animation, so there is nothing to report.
	}
};

/**
 * One digit of the pill, rendered as a two-cell strip inside a one-cell window.
 *
 * The strip rests one cell up, so the cell on show is the second one — the current
 * digit — and the first holds whatever it replaced. Each change bumps `rollId`, which
 * re-keys the strip and replays the roll from `translateY(0)`: the old digit is on screen
 * for the first frame and travels up and out as the new one takes its place. Re-keying
 * rather than transitioning means a change landing mid-roll restarts cleanly instead of
 * stranding the strip between cells, and the resting transform keeps the right digit
 * visible when the animation is suppressed for reduced motion.
 */
const RollingDigit: React.FC<{ digit: string }> = ({ digit }) => {
	const [roll, setRoll] = useState({ previous: digit, current: digit, rollId: 0 });

	useValueChangeEffect(digit, () => {
		setRoll((previousRoll) => ({
			previous: previousRoll.current,
			current: digit,
			rollId: previousRoll.rollId + 1,
		}));
	});

	return (
		<span className="bodymedium relative block h-6 w-5 overflow-hidden rounded-sm bg-black/25 text-white">
			<span
				key={roll.rollId}
				className={cn(
					// Literal transform, not `-translate-y-6`: preflight is disabled project-wide, so
					// the base layer that seeds --tw-translate-x/rotate/skew/scale is never emitted and
					// Tailwind's translate utility resolves to `transform: none`. That left the strip
					// resting on the outgoing digit, so every roll snapped back to the old number.
					'flex flex-col [transform:translateY(-24px)]',
					roll.rollId > 0 && 'animate-digitRollUp motion-reduce:animate-none'
				)}
			>
				<span className="flex h-6 w-full items-center justify-center">{roll.previous}</span>
				<span className="flex h-6 w-full items-center justify-center">{roll.current}</span>
			</span>
		</span>
	);
};

const DigitDisplay: React.FC<{ value: number }> = ({ value }) => (
	<span className="flex items-center gap-0.5">
		{String(value)
			.split('')
			// Keyed by position, never by the digit itself — a value-based key would remount the
			// digit on every change and there would be nothing left to animate.
			.map((digit, index) => (
				<RollingDigit key={index} digit={digit} />
			))}
	</span>
);

/**
 * A count the response was expected to carry but may not. `strict` is off and the counts payload
 * has already gained a field once mid-review, so a field this component reads before the backend
 * ships it would otherwise reach `.toLocaleString()` as undefined and throw during render.
 */
const toDisplayCount = (value: number): number => (Number.isFinite(value) ? value : 0);

const StatColumn: React.FC<{ label: string; value: number }> = ({ label, value }) => (
	<div className="flex flex-1 flex-col items-center gap-1 px-4">
		<span className="h4medium text-character-primary">
			{toDisplayCount(value).toLocaleString()}
		</span>
		<span className="footnoteregular uppercase tracking-wide text-character-secondary">
			{label}
		</span>
	</div>
);

const pluralize = (count: number, singular: string, plural: string) => {
	const displayCount = toDisplayCount(count);
	return `${displayCount.toLocaleString()} ${displayCount === 1 ? singular : plural}`;
};

const DuplicateWorkOrdersStopped: React.FC<DuplicateWorkOrdersStoppedProps> = ({
	fetchStoppedCreationCounts,
	downloadStoppedCreationCountsCSV,
}) => {
	const [counts, setCounts] = useState<StoppedCreationCounts>(null);
	const [displayCount, setDisplayCount] = useState(readLastSeenCount);
	const [staticDigitsKey, setStaticDigitsKey] = useState(0);
	const [exporting, setExporting] = useState(false);

	const refreshCounts = () => {
		fetchStoppedCreationCounts()
			.then((stoppedCreationCounts) => {
				const allTime = nullSafeGetOrElse('allTime', stoppedCreationCounts, 0);
				setCounts(stoppedCreationCounts);
				// Snap instead of rolling when the number loses a place, or gains one: digits are
				// keyed by position from the left, so 99 -> 100 would reuse the two 9s and mount a
				// fresh trailing 0, leaving the pill reading "990" for the length of the animation.
				const digitCountChanged = String(allTime).length !== String(displayCount).length;
				if (allTime < displayCount || digitCountChanged) {
					setStaticDigitsKey((previousKey) => previousKey + 1);
				}
				setDisplayCount(allTime);
				writeLastSeenCount(allTime);
			})
			// The pill is seeded from storage before the fetch, so a failure has to take it back
			// down — otherwise a cached count sits in the top bar over a popover that spins
			// forever, with nothing left to correct it now that there is no polling. The stored
			// value stays put: it is still the last count this user saw, and a transient outage
			// should not cost them the roll on their next successful load.
			.catch(() => setDisplayCount(0));
	};

	useMountEffect(refreshCounts);

	const onDownloadCSV = () => {
		setExporting(true);
		downloadStoppedCreationCountsCSV()
			.catch(() => {})
			.then(() => setExporting(false));
	};

	if (displayCount <= MIN_STOPPED_COUNT_TO_DISPLAY) {
		return null;
	}

	const renderPopoverContent = () => {
		if (!counts) {
			return (
				<div className="flex justify-center px-6 py-4">
					<Spin size="small" />
				</div>
			);
		}

		const stoppedPhrase = pluralize(counts.allTime, 'duplicate WO', 'duplicate WOs');
		// Checked for emptiness before it reaches moment: moment.utc(undefined) returns *now* and
		// reports isValid(), so an absent field would silently date the headline to today.
		const firstStoppedCreationAt = nullSafeGetOrElse('firstStoppedCreationAt', counts, '');
		// Formatted in UTC, matching the zone the month and quarter boundaries beside it are cut
		// on, so the day shown is the same for every viewer rather than shifted by their offset.
		const firstStopped = firstStoppedCreationAt ? moment.utc(firstStoppedCreationAt) : null;
		const headline =
			firstStopped && firstStopped.isValid()
				? `We stopped ${stoppedPhrase} since ${firstStopped.format('MMMM D, YYYY')}`
				: `We stopped ${stoppedPhrase}`;

		return (
			<div className="relative flex  flex-col items-center gap-6 text-center">
				<span className="h5medium text-character-primary">{headline}</span>
				<div className="flex w-full items-stretch justify-between">
					<StatColumn label="This Month" value={counts.currentMonth} />
					<div className="w-px self-stretch bg-grey-5" />
					<StatColumn label="This Quarter" value={counts.currentQuarter} />
					<div className="w-px self-stretch bg-grey-5" />
					<StatColumn label="All Time" value={counts.allTime} />
				</div>
				<div className="flex w-full flex-col items-center gap-2">
					<OWButton
						type="primary"
						size="large"
						block={true}
						loading={exporting}
						onClick={onDownloadCSV}
						icon={<DownloadOutlined />}
					>
						Download CSV
					</OWButton>
					<span className="footnoteregular text-character-secondary">
						Full list of duplicates, emailed to you
					</span>
				</div>
			</div>
		);
	};

	return (
		<Popover content={renderPopoverContent()} trigger="click" placement="bottomRight">
			<button
				type="button"
				className="mr-2 flex cursor-pointer items-center gap-2 border-0 bg-transparent px-2 py-0"
			>
				<DigitDisplay key={staticDigitsKey} value={displayCount} />
				<span className="bodymedium text-white">Duplicate WOs Stopped</span>
			</button>
		</Popover>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchStoppedCreationCounts: () =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchStoppedCreationCountsForSupplier()
				: fetchStoppedCreationCountsForBuyer()
		),
	downloadStoppedCreationCountsCSV: () =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? downloadStoppedCreationCountsCSVForSupplier()
				: downloadStoppedCreationCountsCSVForBuyer()
		),
});

export default withUserType(connect(null, mapDispatchToProps)(DuplicateWorkOrdersStopped));
